import { Injectable, effect, inject } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { Subject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { CurrentUserService } from './common/current-user.service';
import { ModuleNotificationService } from './common/module-notification.service';
import { RealTimeEventType } from '../enums/real-time-event-type.enum';

@Injectable({
  providedIn: 'root'
})
export class SignalRService {
  private hubConnection: signalR.HubConnection | undefined;

  public appointmentAdded$ = new Subject<any>();
  public appointmentUpdated$ = new Subject<any>();
  public orderChanged$ = new Subject<any>();

  public userEvent$ = new Subject<any>();
  public userNotificationReceived$ = new Subject<any>();
  public userTaskAdded$ = new Subject<any>();
  public userMessageAdded$ = new Subject<any>();
  public patientMessageAdded$ = new Subject<any>();
  public patientAdded$ = new Subject<any>();
  public invoiceAdded$ = new Subject<any>();

  private currentUserService = inject(CurrentUserService);
  private readonly moduleNotificationService = inject(ModuleNotificationService);
  private currentJoinedPatientId: string | null = null;

  constructor() {
    effect(() => {
      const isLoggedIn = this.currentUserService.isLoggedIn();
      if (isLoggedIn) {
        this.startConnection();
      } else {
        this.stopConnection();
      }
    });
  }

  public startConnection(): void {
    const currentUser = this.currentUserService.get();
    if (!currentUser || !currentUser.jwtToken) return;

    if (this.hubConnection && this.hubConnection.state === signalR.HubConnectionState.Connected) {
      this.joinGroups(currentUser);
      return;
    } else if (this.hubConnection && this.hubConnection.state !== signalR.HubConnectionState.Disconnected) {
      return;
    }

    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(environment.hubUrl, {
        accessTokenFactory: () => currentUser.jwtToken
      })
      .withAutomaticReconnect()
      .build();

    this.hubConnection.onreconnected((_connectionId) => {
      this.currentJoinedPatientId = null;
      const user = this.currentUserService.get();
      if (user) {
        this.joinGroups(user);
      }
    });

    this.hubConnection
      .start()
      .then(() => {
        console.log('SignalR Connection started');
        this.joinGroups(currentUser);
      })
      .catch(err => console.log('Error while starting SignalR connection: ' + err));

    this.addListeners();
  }

  private joinGroups(currentUser: any): void {
    if (!this.hubConnection) return;

    const patientId = currentUser.refId || currentUser.id;
    if (patientId) {
      const patientIdStr = patientId.toString();
      if (this.currentJoinedPatientId === patientIdStr) {
        return;
      }
      this.hubConnection.invoke('JoinPatientGroup', patientIdStr)
        .then(() => {
          this.currentJoinedPatientId = patientIdStr;
        })
        .catch(err => console.error(err));

      this.hubConnection.invoke('JoinUserGroup', patientIdStr)
        .catch(err => console.error(err));

      if (currentUser.id && currentUser.id.toString() !== patientIdStr) {
        this.hubConnection.invoke('JoinUserGroup', currentUser.id.toString())
          .catch(err => console.error(err));
      }
    }
  }

  private addListeners(): void {
    if (!this.hubConnection) return;

    this.hubConnection.on('Appointment', (data: any) => {
      const eventType = data?.eventType || data?.EventType;
      if (
        eventType === RealTimeEventType.AppointmentAdded) {
        this.appointmentAdded$.next(data);
      } else if (eventType === RealTimeEventType.AppointmentUpdated) {
        this.appointmentUpdated$.next(data);
      } else if (
        eventType === RealTimeEventType.AppointmentOrderChanged
      ) {
        this.orderChanged$.next(data);
      }
    });

    this.hubConnection.on('User', (data: any) => {
      this.userEvent$.next(data);
      const eventType = data?.eventType || data?.EventType;
      if (eventType === RealTimeEventType.UserNotificationReceived) {
        this.userNotificationReceived$.next(data);
      } else if (eventType === RealTimeEventType.UserTaskAdded) {
        this.userTaskAdded$.next(data);
      } else if (eventType === RealTimeEventType.UserMessageAdded) {
        this.userMessageAdded$.next(data);
      } else if (eventType === RealTimeEventType.PatientAdded) {
        this.patientAdded$.next(data);
      } else if (eventType === RealTimeEventType.InvoiceAdded) {
        this.invoiceAdded$.next(data);
      }
    });

    this.hubConnection.on('PatientMessages', (data: any) => {
      const eventType = data?.eventType || data?.EventType;
      if (eventType === RealTimeEventType.PatientMessageAdded) {
        this.patientMessageAdded$.next(data);
        this.moduleNotificationService.get();
      }
    });

    this.hubConnection.on(RealTimeEventType.UserNotificationReceived, (data: any) => {
      this.userNotificationReceived$.next(data);
    });
  }

  public stopConnection(): void {
    if (this.hubConnection) {
      this.hubConnection.stop();
    }
  }
}
